import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { serverTimestamp } from 'firebase/firestore';
import { 
  Layout, 
  Typography, 
  Row, 
  Col, 
  Card, 
  Tag, 
  Divider, 
  Button, 
  Skeleton,
  Avatar,
  List,
  Input,
  message,
  Space
} from 'antd';
import { 
  CalendarOutlined, 
  EyeOutlined, 
  CommentOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  ShareAltOutlined,
  LikeOutlined,
  SendOutlined
} from '@ant-design/icons';
import { doc, getDoc, updateDoc, collection, addDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './ArticlePage.css';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;




const ArticlePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(false);
   const [currentUser, setCurrentUser] = useState(null);
  const [views, setViews] = useState(0);







  
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const docRef = doc(db, "blogPosts", id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setArticle({ id: docSnap.id, ...docSnap.data() });
          // Increment view count
          await updateDoc(docRef, {
            views: docSnap.data().views + 1
          });
        } else {
          setArticle(null);
        }
      } catch (error) {
        console.error("Error fetching article: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) {
      // User is signed in
      setCurrentUser({
        uid: user.uid,
        displayName: user.displayName,
        photoURL: user.photoURL
      });
    } else {
      // User is signed out
      setCurrentUser(null);
    }
  });

  return () => unsubscribe();
}, []);

  useEffect(() => {
    if (!id) return;

    const commentsRef = collection(db, "blogPosts", id, "comments");
    const unsubscribe = onSnapshot(commentsRef, (snapshot) => {
      const commentsData = [];
      snapshot.forEach((doc) => {
        commentsData.push({ id: doc.id, ...doc.data() });
      });
      setComments(commentsData);
    });

    return () => unsubscribe();
  }, [id]);

  const handleCommentSubmit = async () => {
  if (newComment.trim() === '') {
    message.warning('Please enter a comment!');
    return;
  }

  try {
    const user = auth.currentUser;
    const commentsRef = collection(db, "blogPosts", id, "comments");
    
    await addDoc(commentsRef, {
      author: user ? user.displayName || "Authenticated User" : "Anonymous User",
      avatar: user ? user.photoURL || "https://randomuser.me/api/portraits/lego/5.jpg" : "https://randomuser.me/api/portraits/lego/5.jpg",
      content: newComment,
      datetime: serverTimestamp(),
      likes: 0,
      userId: user ? user.uid : null
    });

    // Update comment count in the article
    const articleRef = doc(db, "blogPosts", id);
    await updateDoc(articleRef, {
      commentCount: comments.length + 1
    });

    setNewComment('');
    message.success('Comment added successfully!');
  } catch (error) {
    console.error("Error adding comment: ", error);
    message.error('Failed to add comment');
  }
};



  const handleLike = async () => {
    try {
      const articleRef = doc(db, "blogPosts", id);
      if (liked) {
        await updateDoc(articleRef, {
          likes: article.likes - 1
        });
      } else {
        await updateDoc(articleRef, {
          likes: article.likes + 1
        });
      }
      setLiked(!liked);
    } catch (error) {
      console.error("Error updating likes: ", error);
    }
  };

  if (loading) {
    return (
      <Layout className="layout">
        <Header />
        <Content className="article-content">
          <Skeleton active paragraph={{ rows: 10 }} />
        </Content>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout className="layout">
        <Header />
        <Content className="article-content">
          <div className="article-not-found">
            <Title level={2}>Article Not Found</Title>
            <Paragraph>Sorry, the article you're looking for doesn't exist.</Paragraph>
            <Button type="primary" onClick={() => navigate('/blog')}>
              Back to Blog
            </Button>
          </div>
        </Content>
        <Footer />
      </Layout>
    );
  }

  return (
    <Layout className="layout">
      <Header />
      <Content className="article-content">
        {/* Back Button */}
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(-1)}
          className="back-button"
        >
          Back to Blog
        </Button>

        {/* Article Header */}
        <div className="article-header">
          <Tag color="blue" className="article-category">{article.category}</Tag>
          <Title level={1} className="article-title">{article.title}</Title>
          
          <div className="article-meta">
            <div className="author-info">
              <Avatar src={article.author?.avatar || "https://randomuser.me/api/portraits/men/32.jpg"} size={48} />
              <div>
                <Text strong>{article.author?.name || "Admin"}</Text>
                <Text type="secondary">{article.author?.bio || "Healthcare Expert"}</Text>
              </div>
            </div>
            
            <div className="stats">
              <Text type="secondary"><CalendarOutlined /> {article.date}</Text>
              <Text type="secondary"><EyeOutlined /> {article.views} views</Text>
              <Text type="secondary"><CommentOutlined /> {comments.length} comments</Text>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className="featured-image-container">
  {article.image ? (
    <img 
      src={article.image} 
      alt={article.title} 
      className="featured-image"
      onError={(e) => {
        e.target.onerror = null; 
        e.target.src = 'https://images.unsplash.com/photo-1581056771107-24ca5f033842?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80';
      }}
    />
  ) : (
    <div 
      className="featured-image-placeholder"
      style={{ 
        backgroundImage: `url(https://images.unsplash.com/photo-1581056771107-24ca5f033842?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80)` 
      }}
    />
  )}
</div>

        {/* Article Content */}
        <div className="article-body">
  {article.content.split('<br>').map((paragraph, i) => (
    <p 
      key={i} 
      dangerouslySetInnerHTML={{ __html: paragraph }} 
      style={{ whiteSpace: 'pre-wrap' }}
    />
  ))}
</div>

        {/* Article Footer */}
        <div className="article-footer">
          <Button 
            type={liked ? 'primary' : 'default'} 
            icon={<LikeOutlined />} 
            onClick={handleLike}
          >
            {liked ? 'Liked' : 'Like'} ({article.likes || 0})
          </Button>
          
          <Button 
            type="text" 
            icon={<ShareAltOutlined />}
            onClick={() => message.info('Share functionality coming soon!')}
          >
            Share
          </Button>
        </div>

        {/* Comments Section */}
        <div className="comments-section">
          <Title level={3}>Comments ({comments.length})</Title>
          
          <List
            className="comment-list"
            itemLayout="horizontal"
            dataSource={comments}
            renderItem={comment => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar src={comment.avatar} />}
                  title={comment.author}
                  description={
                    <>
                      <p>{comment.content}</p>
                      <Text type="secondary">{new Date(comment.datetime).toLocaleDateString()}</Text>
                    </>
                  }
                />
              </List.Item>
            )}
          />

          <div className="comment-form">
            <TextArea
              rows={4}
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Write your comment here..."
            />
            <Button 
              type="primary" 
              icon={<SendOutlined />}
              onClick={handleCommentSubmit}
              style={{ marginTop: 16 }}
            >
              Post Comment
            </Button>
          </div>
        </div>

        {/* Related Articles */}
        {/* {article.relatedArticles && article.relatedArticles.length > 0 && (
          <div className="related-articles">
            <Divider orientation="left">
              <Title level={4}>Related Articles</Title>
            </Divider>
            
            <Row gutter={[24, 24]}>
              {article.relatedArticles.map(postId => (
                <Col xs={24} sm={12} md={8} key={postId}>
                  <Card
                    hoverable
                    className="related-article-card"
                    onClick={() => navigate(`/article/${postId}`)}
                    cover={
                      <div 
                        className="related-article-image" 
                        style={{ backgroundImage: `url(${article.relatedArticlesImages?.[postId] || 'https://images.unsplash.com/photo-1581056771107-24ca5f033842?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80'})` }}
                      />
                    }
                  >
                    <Tag color="blue" className="post-category">{article.relatedArticlesCategories?.[postId] || 'Healthcare'}</Tag>
                    <Title level={4} className="post-title">{article.relatedArticlesTitles?.[postId] || 'Related Article'}</Title>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )} */}
      </Content>
      <Footer />
    </Layout>
  );
};

export default ArticlePage;